<script setup lang="ts">
import { createPopper, type Instance, type Placement } from "@popperjs/core";
import {
  ref,
  watchEffect,
  watch,
  nextTick,
  type Ref,
  type WatchStopHandle,
} from "vue";
import useHover from "../composables/useHover";
import { onOpened } from "../composables/onOpened";
import { platform } from "../utils/platform";

interface Props {
  reference: null | HTMLElement;
  placement?: Placement;
}

const props = withDefaults(defineProps<Props>(), {
  reference: null,
  placement: "bottom",
});

let popper: Instance | null = null;
let hoverRef: Ref<boolean> = ref(false);
let hoverTip: Ref<boolean> = ref(false);
let watchStop: WatchStopHandle = () => { };
const show = ref(false);
const tooltip = ref<HTMLElement | null>(null);
let scrolledParent: HTMLElement | Document | null = null;
const destroyTooltip = () => {
  show.value = false;
  popper?.destroy();
};

const useOpenTooltip = (hoverRef: Ref<boolean>, hoverTip: Ref<boolean>) => {
  return watch([hoverRef, hoverTip], (next) => {
    if (next[0] || next[1]) {
      setTimeout(() => {
        if (hoverRef.value || hoverTip.value) {
          if (tooltip.value && props.reference) {
            popper = createPopper(props.reference, tooltip.value, {
              placement: props.placement,
              strategy: "fixed",
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
            });
            nextTick(() => {
              show.value = true;
              popper?.update();
              if (props.reference) {
                scrolledParent = getParentScroll(props.reference);
              }
              if (scrolledParent) {
                scrolledParent.addEventListener("scroll", destroyTooltip);
              }
            });
          }
        }
      }, 1000);
    } else if (next[0] === false && next[1] === false) {
      show.value = false;
      popper?.destroy();
      scrolledParent?.removeEventListener("scroll", destroyTooltip);
    }
  });
};

const getParentScroll = (el: HTMLElement) => {
  if (!el.parentElement) return document;
  if (el.parentElement) {
    if (el.parentElement.classList.contains("simplebar-content-wrapper"))
      return el.parentElement;
    else return document;
  } else return document;
};

if (platform !== "mobile")
  watchEffect(() => {
    if (tooltip.value && props.reference) {
      // eslint-disable-next-line vue/no-ref-as-operand
      hoverRef = useHover(ref(props.reference)).state;
      // eslint-disable-next-line vue/no-ref-as-operand
      hoverTip = useHover(tooltip).state;
      show.value = false;
      watchStop = useOpenTooltip(hoverRef, hoverTip);
    } else {
      if (watchStop) {
        watchStop();
      }
    }
  });

const exist = ref(false);
if (platform !== "mobile")
  onOpened(
    () => {
      show.value = false;
      exist.value = true;
    },
    () => {
      show.value = false;
      if (hoverRef.value) {
        hoverRef.value = false;
      }
      exist.value = false;
      if (watchStop) {
        watchStop();
      }
    },
  );
</script>
<template>
  <teleport v-if="exist && platform !== 'mobile'" to="body">
    <div ref="tooltip" class="tooltip-main" :class="{
      show: show,
    }">
      <div class="space" />
      <div class="tooltip">
        <slot />
      </div>
    </div>
  </teleport>
</template>
<style scoped>
.tooltip-main {
  display: none;
}

.space {
  height: rem(8);
}

.tooltip {
  max-width: 450px;
  overflow-wrap: break-word;
  background-color: rgba(41, 41, 41, 0.85);
  color: #ececec;
  border-radius: 7px;
  padding: 8px 15px;
}

.tooltip-main.show {
  display: block;
  z-index: 10000;
}
</style>
